import { apiFetch } from '../lib/apiFetch';

export interface TreeSpecies {
  id: string;
  commonName: string;
  isActive: boolean;
}

export async function fetchTreeSpecies(): Promise<TreeSpecies[]> {
  const res = await apiFetch('/api/v1/settings/tree-species');
  if (!res.ok) throw new Error('Failed to fetch tree species');
  const data = await res.json();
  return data.species;
}

// ─── Workshop Type Pricing ─────────────────────────────────────────────────────

export interface WorkshopTypeRegionPrice {
  regionId: string;
  regionName: string;
  price: string;
}

export interface WorkshopTypeSetting {
  id: string;
  name: string;
  displayName: string | null;
  treeSpeciesId: string | null;
  treeSpecies?: { id: string; commonName: string } | null;
  description?: string | null;
  defaultDurationMinutes?: number | null;
  defaultPrice: string | null;
  defaultCapacity: number | null;
  coveredByTreeGuarantee?: boolean;
  isActive?: boolean;
  regionPrices: WorkshopTypeRegionPrice[];
}

type RawTypeRegionPrice = { regionId: string; price: string; region: { name: string } };

function normalizeTypeSetting(raw: {
  id: string;
  name: string;
  displayName: string | null;
  treeSpeciesId: string | null;
  treeSpecies?: { id: string; commonName: string } | null;
  defaultPrice: string | null;
  defaultCapacity: number | null;
  coveredByTreeGuarantee?: boolean;
  regionPrices: RawTypeRegionPrice[];
  description?: string | null;
  defaultDurationMinutes?: number | null;
  isActive?: boolean;
}): WorkshopTypeSetting {
  return {
    ...raw,
    regionPrices: raw.regionPrices.map((rp) => ({
      regionId: rp.regionId,
      regionName: rp.region.name,
      price: rp.price,
    })),
  };
}

export async function fetchWorkshopTypeSettings(): Promise<WorkshopTypeSetting[]> {
  const res = await apiFetch('/api/v1/settings/workshop-types');
  if (!res.ok) throw new Error('Failed to fetch workshop type settings');
  const data = await res.json();
  return data.workshopTypes.map(normalizeTypeSetting);
}

export async function fetchWorkshopTypeSetting(id: string): Promise<WorkshopTypeSetting> {
  const res = await apiFetch(`/api/v1/settings/workshop-types/${id}`);
  if (!res.ok) throw new Error('Failed to fetch workshop type');
  const data = await res.json();
  return normalizeTypeSetting(data.workshopType);
}

export async function updateWorkshopTypeSetting(
  id: string,
  defaultPrice: string | null,
  defaultCapacity: number | null,
): Promise<WorkshopTypeSetting> {
  const res = await apiFetch(`/api/v1/settings/workshop-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ defaultPrice, defaultCapacity }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to update workshop type pricing');
  return normalizeTypeSetting(data.workshopType);
}

export async function upsertWorkshopTypeRegionPrice(
  typeId: string,
  regionId: string,
  price: string,
): Promise<WorkshopTypeRegionPrice> {
  const res = await apiFetch(
    `/api/v1/settings/workshop-types/${typeId}/region-prices/${regionId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ price }),
    },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to save regional price');
  return {
    regionId: data.regionPrice.regionId,
    regionName: data.regionPrice.region.name,
    price: data.regionPrice.price,
  };
}

export async function deleteWorkshopTypeRegionPrice(
  typeId: string,
  regionId: string,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/settings/workshop-types/${typeId}/region-prices/${regionId}`,
    {
      method: 'DELETE',
    },
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to delete regional price');
  }
}
